import { useState } from 'react';
import { Bell, Slack, Mail, X, Plus } from 'lucide-react';
import { useNotificationConfigs, useUpdateNotificationConfig } from '@/hooks/useNotifications';
import { useSlackStatus } from '@/hooks/useSlack';
import { Button, Card, Input, Skeleton } from '@solid/ui';
import { Switch } from '@/components/ui/Switch';
import { Badge } from '@/components/ui/Badge';

const EVENT_LABELS: Record<string, { label: string; description: string }> = {
  DEAL_CREATED: { label: 'Deal Created', description: 'When a new deal is created' },
  DEAL_WON: { label: 'Deal Won', description: 'When a deal is marked as won' },
  DEAL_LOST: { label: 'Deal Lost', description: 'When a deal is closed/lost' },
  DEAL_STAGE_CHANGED: { label: 'Stage Changed', description: 'When a deal moves to a new stage' },
  FORM_SUBMITTED: { label: 'Form Submitted', description: 'When a new form submission is received' },
};

export default function NotificationSettings() {
  const { data: configs = [], isLoading } = useNotificationConfigs();
  const { data: slackStatus } = useSlackStatus();
  const updateConfig = useUpdateNotificationConfig();
  const [editingEmails, setEditingEmails] = useState<{ event: string; emails: string[] } | null>(null);
  const [emailInput, setEmailInput] = useState('');

  const handleToggleSlack = async (event: string, enabled: boolean) => {
    const config = configs.find((c) => c.event === event);
    await updateConfig.mutateAsync({
      event,
      channels: { ...config?.channels, slack: enabled },
    });
  };

  const handleToggleActive = async (event: string, isActive: boolean) => {
    await updateConfig.mutateAsync({ event, isActive });
  };

  const handleEditEmails = (event: string, emails: string[]) => {
    setEditingEmails({ event, emails: [...emails] });
    setEmailInput('');
  };

  const handleAddEmail = () => {
    if (!editingEmails || !emailInput.trim()) return;
    if (!emailInput.includes('@')) return;
    setEditingEmails({
      ...editingEmails,
      emails: [...editingEmails.emails, emailInput.trim()],
    });
    setEmailInput('');
  };

  const handleRemoveEmail = (email: string) => {
    if (!editingEmails) return;
    setEditingEmails({
      ...editingEmails,
      emails: editingEmails.emails.filter((e) => e !== email),
    });
  };

  const handleSaveEmails = async () => {
    if (!editingEmails) return;
    const config = configs.find((c) => c.event === editingEmails.event);
    await updateConfig.mutateAsync({
      event: editingEmails.event,
      channels: { ...config?.channels, email: editingEmails.emails },
    });
    setEditingEmails(null);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-heading-2 text-secondary flex items-center gap-2">
          <Bell className="w-5 h-5" />
          Notifications
        </h2>
        <p className="text-body-sm text-grey-500 mt-1">
          Configure which events trigger notifications and where they're sent.
        </p>
      </div>

      {/* Channel Status */}
      <Card padding="sm">
        <h3 className="text-sm font-medium text-secondary mb-3">Channels</h3>
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <Slack className="w-5 h-5 text-[#4A154B]" />
            <span className="text-sm">Slack</span>
            {slackStatus?.isActive ? (
              <Badge variant="success">Connected</Badge>
            ) : (
              <Badge variant="default">Not Connected</Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-500" />
            <span className="text-sm">Email</span>
            <Badge variant="success">Available</Badge>
          </div>
        </div>
      </Card>

      {/* Event Configuration */}
      <div className="space-y-3">
        {configs.map((config) => {
          const meta = EVENT_LABELS[config.event] || { label: config.event, description: '' };
          const isEditing = editingEmails?.event === config.event;

          return (
            <Card key={config.id} padding="sm">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={config.isActive}
                      onCheckedChange={(checked) => handleToggleActive(config.event, checked)}
                    />
                    <div>
                      <h4 className="font-medium text-secondary">{meta.label}</h4>
                      <p className="text-xs text-grey-500">{meta.description}</p>
                    </div>
                  </div>

                  {config.isActive && (
                    <div className="mt-4 ml-10 space-y-3">
                      {/* Slack Toggle */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Slack className="w-4 h-4 text-[#4A154B]" />
                          <span className="text-sm">Slack</span>
                        </div>
                        <Switch
                          checked={config.channels.slack}
                          onCheckedChange={(checked) => handleToggleSlack(config.event, checked)}
                          disabled={!slackStatus?.isActive}
                        />
                      </div>

                      {/* Email Recipients */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-blue-500" />
                            <span className="text-sm">Email Recipients</span>
                          </div>
                          {!isEditing && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditEmails(config.event, config.channels.email || [])}
                            >
                              Edit
                            </Button>
                          )}
                        </div>

                        {isEditing ? (
                          <div className="space-y-2">
                            <div className="flex gap-2">
                              <Input
                                className="flex-1"
                                aria-label="Email address"
                                value={emailInput}
                                onChange={(e) => setEmailInput(e.target.value)}
                                placeholder="email@example.com"
                                onKeyDown={(e) => e.key === 'Enter' && handleAddEmail()}
                              />
                              <Button size="sm" onClick={handleAddEmail} aria-label="Add email">
                                <Plus className="w-4 h-4" />
                              </Button>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {editingEmails.emails.map((email) => (
                                <span
                                  key={email}
                                  className="inline-flex items-center gap-1 px-2 py-1 bg-grey-100 rounded text-sm"
                                >
                                  {email}
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveEmail(email)}
                                    aria-label={`Remove ${email}`}
                                  >
                                    <X className="w-3 h-3 text-grey-500 hover:text-red-500" />
                                  </button>
                                </span>
                              ))}
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" onClick={handleSaveEmails}>
                                Save
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => setEditingEmails(null)}>
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {config.channels.email?.length ? (
                              config.channels.email.map((email) => (
                                <Badge key={email} variant="default">
                                  {email}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-xs text-grey-400">No recipients</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
